"use client";

import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Separator } from "@/components/ui/separator";

export type FooterNavItem = {
	href: string;
	label: string;
	newTab?: boolean;
};

export type FooterCopyrightBrand = {
	label: string;
	href: string | null;
	newTab?: boolean;
};

export type Footer2Props = {
	navLinks: FooterNavItem[];
	legalLinks: FooterNavItem[];
	copyrightBrand: FooterCopyrightBrand | null;
};

export function Footer2({
	navLinks,
	legalLinks,
	copyrightBrand,
}: Footer2Props) {
	const pathname = usePathname();
	const linkExtras = (newTab?: boolean) =>
		newTab
			? { rel: "noopener noreferrer" as const, target: "_blank" as const }
			: {};
	const handleHomeClick = () => {
		if (pathname !== "/") return;
		window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
	};
	const getHomeLinkProps = (href?: string | null) =>
		href === "/" ? { onClick: handleHomeClick, scroll: true } : {};

	return (
		<footer
			aria-label="Site footer"
			className="bg-black section-padding-y mt-16 text-sm text-white"
			role="contentinfo"
		>
			<div className="container-padding-x container mx-auto flex flex-col gap-12 lg:gap-16">
				<div className="flex flex-col gap-12">
					<div className="flex flex-col items-center justify-between gap-12 text-center lg:flex-row">
						<p className="text-muted-foreground order-2 md:order-1">
							{/* <span>Copyright © {new Date().getFullYear()}</span> */}
							{copyrightBrand ? (
								<>
									{" "}
									{copyrightBrand.href ? (
										<Link
											className="hover:underline"
											href={copyrightBrand.href}
											{...getHomeLinkProps(copyrightBrand.href)}
											{...linkExtras(copyrightBrand.newTab)}
										>
											{copyrightBrand.label}
										</Link>
									) : (
										<span>{copyrightBrand.label}</span>
									)}
									.
								</>
							) : null}{" "}
							All rights reserved.
						</p>

						{legalLinks.length > 0 ? (
							<nav
								aria-label="Legal links"
								className="order-1 flex flex-col items-center gap-4 md:order-2 md:flex-row md:gap-8"
							>
								{legalLinks.map((link) => (
									<Link
										key={`${link.href}-${link.label}`}
										className="text-muted-foreground hover:text-foreground transition-colors"
										href={link.href}
										{...getHomeLinkProps(link.href)}
										{...linkExtras(link.newTab)}
									>
										{link.label}
									</Link>
								))}
							</nav>
						) : null}
					</div>
					<Separator role="presentation" />

					<div className="flex flex-col gap-12 md:items-center md:justify-center lg:flex-row">
						<div className="flex flex-col items-center gap-12 lg:flex-row">
							<div className="flex flex-row items-center gap-6 sm:gap-8">
								<Link
									aria-label="Go to homepage"
									className="inline-flex shrink-0"
									href="/"
									{...getHomeLinkProps("/")}
								>
									<Image
										alt="Brooklyn 500"
										className="h-10 w-auto max-w-[min(100%,280px)] object-contain md:h-13"
										height={80}
										src="/brooklyn-brewery-logo.png"
										width={280}
									/>
								</Link>
								<Link
									aria-label="Regnbågsfonden (opens in new tab)"
									className="inline-flex shrink-0"
									href="https://regnbagsfonden.se"
									rel="noopener noreferrer"
									target="_blank"
								>
									<Image
										alt="Regnbågsfonden"
										className="h-10 w-auto max-w-[min(100%,280px)] object-contain md:h-12"
										height={80}
										src="/regnbagsfonden.svg"
										width={280}
									/>
								</Link>
							</div>

							{navLinks.length > 0 ? (
								<nav
									aria-label="Footer navigation"
									className="flex flex-col items-center gap-4 text-center md:flex-row md:gap-8"
								>
									{navLinks.map((link) => (
										<Link
											key={`${link.href}-${link.label}`}
											className="text-muted-foreground hover:text-foreground transition-colors"
											href={link.href}
											{...getHomeLinkProps(link.href)}
											{...linkExtras(link.newTab)}
										>
											{link.label}
										</Link>
									))}
								</nav>
							) : null}
						</div>
					</div>
				</div>
			</div>
		</footer>
	);
}
